import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useWebSocket, WebSocketMessage } from '@/hooks/useWebSocket';
import { api } from '@/api';
import { Match, Participant } from '@/types/api.types';
import { logger } from '@/utils/logger';
import { compareMatchesForOrder } from '@/utils/matchOrder';
import { TournamentService } from '@/services/tournament.service';
import { getUploadsBaseUrl, normalizeUploadImageUrl } from '@/config/api.config';
import styled from 'styled-components';

const BroadcastContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  color: #1a1a1a;
  font-family: 'Arial', sans-serif;
  overflow: hidden;
`;

const TimerSection = styled.div`
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid #e0e0e0;
  position: relative;
`;

const TatamiLabel = styled.div`
  font-size: 2rem;
  font-weight: 500;
  margin-bottom: 20px;
  color: #666666;
`;

const TimerDisplay = styled.div`
  font-size: 8rem;
  font-weight: 300;
  letter-spacing: 0.1em;
  color: #1a1a1a;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 40px;
  gap: 40px;
  background: #f5f5f5;
`;

const CurrentMatchSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 8px;
  padding: 40px;
  border: 1px solid #e0e0e0;
`;

const SectionTitle = styled.div`
  font-size: 1.8rem;
  font-weight: 500;
  margin-bottom: 8px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #666666;
`;

const CategoryLabel = styled.div`
  font-size: 1.2rem;
  font-weight: 400;
  margin-bottom: 30px;
  text-align: center;
  letter-spacing: 0.02em;
  color: #888888;
`;

const ParticipantsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex: 1;
  gap: 40px;
`;

const ParticipantCard = styled.div`
  flex: 1;
  background: #fafafa;
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  border: 1px solid #e0e0e0;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ParticipantName = styled.div`
  font-size: 2.5rem;
  font-weight: 400;
  margin-bottom: 15px;
  color: #1a1a1a;
`;

const ParticipantClub = styled.div`
  font-size: 1.2rem;
  color: #999999;
  margin-top: 10px;
`;

const VS = styled.div`
  font-size: 3rem;
  font-weight: 300;
  color: #999999;
`;

const NextMatchSection = styled.div`
  height: 25vh;
  background: #ffffff;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  border: 1px solid #e0e0e0;
`;

const NextMatchTitle = styled.div`
  font-size: 1.5rem;
  font-weight: 500;
  margin-bottom: 4px;
  text-align: center;
  color: #666666;
`;

const NextMatchCategory = styled.div`
  font-size: 1rem;
  font-weight: 400;
  margin-bottom: 15px;
  text-align: center;
  color: #888888;
`;

const NextMatchInfo = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex: 1;
  font-size: 1.3rem;
  color: #1a1a1a;
`;

const EmptyState = styled.div`
  text-align: center;
  font-size: 1.5rem;
  color: #999999;
  padding: 40px;
`;

const BroadcastHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 12px 24px;
  background: #ffffff;
  border-bottom: 1px solid #e0e0e0;
  min-height: 56px;
  flex-shrink: 0;
`;

const TournamentLogo = styled.img`
  height: 40px;
  width: auto;
  max-width: 120px;
  object-fit: contain;
`;

const TournamentName = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a1a1a;
  letter-spacing: 0.02em;
`;

export const MatchBroadcast: React.FC = () => {
  const { id: tournamentId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const matchIdParam = searchParams.get('matchId');
  const matchId = matchIdParam ? parseInt(matchIdParam, 10) : undefined;
  const [currentMatch, setCurrentMatch] = useState<Match | null>(null);
  const [nextMatch, setNextMatch] = useState<Match | null>(null);
  const [currentDisplayNum, setCurrentDisplayNum] = useState<number | null>(null);
  const [nextDisplayNum, setNextDisplayNum] = useState<number | null>(null);
  const [timerSeconds, setTimerSeconds] = useState<number>(0);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [tatamiNumber, setTatamiNumber] = useState<number | null>(null);
  const [tournamentInfo, setTournamentInfo] = useState<{ name: string; coverImage?: string } | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const pausedTimeRef = useRef<number>(0);

  function getCoverImageUrl(coverImage: string | undefined): string | undefined {
    if (!coverImage) return undefined;
    const raw =
      coverImage.startsWith('http')
        ? coverImage
        : `${getUploadsBaseUrl()}${coverImage.startsWith('/') ? coverImage : '/' + coverImage}`;
    return normalizeUploadImageUrl(raw);
  }

  // Загрузка названия и логотипа турнира
  useEffect(() => {
    if (!tournamentId) return;
    TournamentService.getById(Number(tournamentId))
      .then((t) => setTournamentInfo({ name: t.name, coverImage: t.coverImage }))
      .catch((err) => logger.warn('MatchBroadcast: failed to load tournament info', err));
  }, [tournamentId]);

  // Автоматический переход в полноэкранный режим при загрузке
  useEffect(() => {
    const enterFullscreen = () => {
      try {
        if (document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen();
        } else if ((document.documentElement as any).webkitRequestFullscreen) {
          (document.documentElement as any).webkitRequestFullscreen();
        } else if ((document.documentElement as any).mozRequestFullScreen) {
          (document.documentElement as any).mozRequestFullScreen();
        } else if ((document.documentElement as any).msRequestFullscreen) {
          (document.documentElement as any).msRequestFullscreen();
        }
      } catch (error) {
        logger.warn('Could not enter fullscreen mode:', error);
      }
    };

    // Пытаемся войти в полноэкранный режим при загрузке
    const timer = setTimeout(enterFullscreen, 500);
    
    // Также обрабатываем нажатие F11
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'F11') {
        e.preventDefault();
        enterFullscreen();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  // Загрузка текущего матча (татами обязателен — номера 1,2,3… как в Live Табло)
  useEffect(() => {
    const loadMatch = async () => {
      if (!tournamentId) return;
      const tatamiParam = searchParams.get('tatami');
      const tatamiNum = tatamiParam ? parseInt(tatamiParam, 10) : null;
      
      // Сбрасываем предыдущие данные при изменении татами
      if (tatamiNumber !== null && tatamiNumber !== tatamiNum) {        
        setCurrentMatch(null);
        setNextMatch(null);
        setCurrentDisplayNum(null);
        setNextDisplayNum(null);
      }
      
      setTatamiNumber(tatamiNum);
      if (!tatamiNum) {
        logger.warn('MatchBroadcast: No tatami number in URL');
        return;
      }

      try {
        // Добавляем параметр для предотвращения кэширования
        const cacheBuster = `?t=${Date.now()}`;
        const response = await api.get(`/tournaments/${tournamentId}/matches${cacheBuster}`);
        const matches = response.data?.data || response.data || [];
        const filteredMatches = matches.filter((m: Match) => m.tatamiNumber === tatamiNum);
        
        logger.debug('MatchBroadcast: Total matches:', matches.length, 'Filtered for tatami', tatamiNum, ':', filteredMatches.length, 'matches:', filteredMatches.map((m: Match) => ({ id: m.id, matchNumber: m.matchNumber, tatamiNumber: m.tatamiNumber })));
              const liveMatches = filteredMatches.filter(
                (m: Match) => m.status === 'in_progress' || m.status === 'pending'
              );
              const liveSorted = [...liveMatches].sort((a, b) => {
          if (a.status === 'in_progress' && b.status === 'pending') return -1;
          if (a.status === 'pending' && b.status === 'in_progress') return 1;
          return compareMatchesForOrder(a, b);
        });

        let match: Match | null = null;
        if (matchId) {
          match = filteredMatches.find((m: Match) => m.id === matchId) || null;
        } else {
          match = liveSorted[0] || null;
        }
        setCurrentMatch(match);

        // Находим следующий матч
        let next: Match | null = null;
        if (match) {
          const currentIndex = liveSorted.findIndex((m) => m.id === match.id);
          if (currentIndex >= 0 && currentIndex < liveSorted.length - 1) {
            next = liveSorted[currentIndex + 1];
          } else if (match.matchNumber) {
            // Если текущий матч не в liveSorted (например, completed), ищем следующий по matchNumber
            const nextByNumber = liveSorted.find((m: Match) => 
              m.matchNumber && m.matchNumber > match.matchNumber!
            );
            if (nextByNumber) {
              next = nextByNumber;
            } else if (liveSorted.length > 0) {
              // Если не нашли по номеру, берем первый из live
              next = liveSorted[0];
            }
          } else if (liveSorted.length > 0) {
            // Если нет matchNumber, берем первый из live
            next = liveSorted[0];
          }
        }
        setNextMatch(next);

        // Используем matchNumber, если нет - categoryOrderOnTatami (порядковый номер на татами), иначе id
        setCurrentDisplayNum(
          match?.matchNumber ?? 
          match?.categoryOrderOnTatami ?? 
          (match?.id ? match.id : null)
        );
        setNextDisplayNum(
          next?.matchNumber ?? 
          next?.categoryOrderOnTatami ?? 
          (next?.id ? next.id : null)
        );
      } catch (error) {
        logger.error('Error loading match:', error);
      }
    };

    loadMatch();
    const interval = setInterval(loadMatch, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId, matchId, searchParams.toString()]);

  // WebSocket для синхронизации секундомера и обновлений матчей
  useWebSocket({
    tournamentId: Number(tournamentId),
    enabled: !!tournamentId,
    onMessage: (message: WebSocketMessage) => {
      if (message.type === 'timer_control') {
        const { action, seconds } = message.data || {};
        
        if (action === 'start') {          
          // Устанавливаем startTimeRef на основе полученного времени
          if (seconds !== undefined) {
            setTimerSeconds(seconds);
            startTimeRef.current = Date.now() - (seconds * 1000);
          } else {
            // Если секунды не переданы, используем текущее значение
            startTimeRef.current = Date.now() - (timerSeconds * 1000);
          }
          pausedTimeRef.current = 0;
          setIsPaused(false);
          setIsRunning(true); // Устанавливаем isRunning в последнюю очередь, чтобы useEffect сработал
        } else if (action === 'pause') {
          setIsRunning(false);
          setIsPaused(true);
          // Сохраняем текущее время при паузе
          if (seconds !== undefined) {
            pausedTimeRef.current = seconds;
            setTimerSeconds(seconds);
          } else {
            pausedTimeRef.current = timerSeconds;
          }
          // Останавливаем отсчет времени
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          startTimeRef.current = null;
        } else if (action === 'reset') {
          setIsRunning(false);
          setIsPaused(false);
          setTimerSeconds(0);
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          startTimeRef.current = null;
          pausedTimeRef.current = 0;
        } else if (action === 'update' && seconds !== undefined) {
          setTimerSeconds(seconds);
          if (isRunning && !isPaused) {
            // Всегда пересчитываем startTimeRef по данным источника (каждую секунду),
            // чтобы трансляция не уходила вперёд/назад относительно панели управления
            startTimeRef.current = Date.now() - (seconds * 1000);
          }
        } else if (action === 'match_changed' && message.data?.matchId) {
          const newMatchId = message.data.matchId;
          const nextMatchId = message.data?.nextMatchId;
          const loadMatch = async () => {
            if (!tournamentId) return;
            const tatamiParam = searchParams.get('tatami');
            const tatamiNum = tatamiParam ? parseInt(tatamiParam, 10) : null;
            
            if (!tatamiNum) return;
            try {
              // Задержка для получения обновленных данных с сервера (следующий матч с обновленными участниками)
              await new Promise(resolve => setTimeout(resolve, 500));
              
              const response = await api.get(`/tournaments/${tournamentId}/matches`);
              const matches = response.data?.data || response.data || [];
              const filteredMatches = matches.filter((m: Match) => m.tatamiNumber === tatamiNum);
              const liveMatches = filteredMatches.filter(
                (m: Match) => m.status === 'in_progress' || m.status === 'pending'
              );
              const liveSorted = [...liveMatches].sort((a, b) => {
                if (a.status === 'in_progress' && b.status === 'pending') return -1;
                if (a.status === 'pending' && b.status === 'in_progress') return 1;
                return compareMatchesForOrder(a, b);
              });
              
              // Находим новый текущий матч
              const newMatch = filteredMatches.find((m: Match) => m.id === newMatchId);
              if (newMatch) {
                setCurrentMatch(newMatch);
                setTimerSeconds(0);
                setIsRunning(false);
                setIsPaused(false);
                if (timerIntervalRef.current) {
                  clearInterval(timerIntervalRef.current);
                  timerIntervalRef.current = null;
                }
                startTimeRef.current = null;
                
                // Находим следующий матч
                let next: Match | null = null;
                
                // Если передан nextMatchId, используем его
                if (nextMatchId) {
                  next = filteredMatches.find((m: Match) => m.id === nextMatchId) || null;
                }
                
                // Если не нашли по ID, ищем по порядку
                if (!next) {
                  const currentIndex = liveSorted.findIndex((m) => m.id === newMatch.id);
                  next = currentIndex >= 0 && currentIndex < liveSorted.length - 1
                    ? liveSorted[currentIndex + 1]
                    : null;
                }
                
                setNextMatch(next);
                
                // Используем matchNumber, если нет - categoryOrderOnTatami (порядковый номер на татами), иначе id
                setCurrentDisplayNum(
                  newMatch?.matchNumber ?? 
                  newMatch?.categoryOrderOnTatami ?? 
                  (newMatch?.id ? newMatch.id : null)
                );
                setNextDisplayNum(
                  next?.matchNumber ?? 
                  next?.categoryOrderOnTatami ?? 
                  (next?.id ? next.id : null)
                );
                
                const nextParams = new URLSearchParams(searchParams);
                nextParams.set('matchId', String(newMatchId));
                setSearchParams(nextParams, { replace: true });
              }
            } catch (error) {
              logger.error('Error loading match after change:', error);
            }
          };
          loadMatch();
        }
      } else if (message.type === 'match_updated' || message.type === 'match_completed') {
        // Обновляем данные текущего и следующего матча при получении обновлений
        const updatedMatch = message.data?.match || message.data;
        if (updatedMatch && tournamentId) {
          const loadMatch = async () => {
            const tatamiParam = searchParams.get('tatami');
            const tatamiNum = tatamiParam ? parseInt(tatamiParam, 10) : null;
            
            if (!tatamiNum) return;
            
            try {
              const response = await api.get(`/tournaments/${tournamentId}/matches`);
              const matches = response.data?.data || response.data || [];
              const filteredMatches = matches.filter((m: Match) => m.tatamiNumber === tatamiNum);
              
              // Обновляем текущий матч, если он был изменен
              if (currentMatch && updatedMatch.id === currentMatch.id) {
                const updated = filteredMatches.find((m: Match) => m.id === currentMatch.id);
                if (updated) {
                  setCurrentMatch(updated);
                }
              }
              
              // Обновляем следующий матч, если он был изменен
              if (nextMatch && updatedMatch.id === nextMatch.id) {
                const updated = filteredMatches.find((m: Match) => m.id === nextMatch.id);
                if (updated) {
                  setNextMatch(updated);
                }
              }
              
              // Если обновленный матч - это следующий матч для текущего, обновляем его
              const liveMatches = filteredMatches.filter(
                (m: Match) => m.status === 'in_progress' || m.status === 'pending'
              );
              const liveSorted = [...liveMatches].sort((a, b) => {
                if (a.status === 'in_progress' && b.status === 'pending') return -1;
                if (a.status === 'pending' && b.status === 'in_progress') return 1;
                return compareMatchesForOrder(a, b);
              });
              
              if (currentMatch) {
                const currentIndex = liveSorted.findIndex((m) => m.id === currentMatch.id);
                if (currentIndex >= 0 && currentIndex < liveSorted.length - 1) {
                  const next = liveSorted[currentIndex + 1];
                  if (next && (!nextMatch || next.id !== nextMatch.id)) {
                    setNextMatch(next);
                    setNextDisplayNum(
                      next?.matchNumber ?? 
                      next?.categoryOrderOnTatami ?? 
                      (next?.id ? next.id : null)
                    );
                  }
                }
              }
            } catch (error) {
              logger.error('Error updating match data:', error);
            }
          };
          loadMatch();
        }
      }
    },
  });

  // Логика секундомера - запускается при изменении isRunning или isPaused
  useEffect(() => {
    // Очищаем интервал перед созданием нового
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    if (isRunning && !isPaused) {
      // Если startTimeRef не установлен, устанавливаем его на основе текущего времени
      if (startTimeRef.current === null) {
        const currentSeconds = timerSeconds || 0;
        startTimeRef.current = Date.now() - (currentSeconds * 1000);        
      }
      
      timerIntervalRef.current = setInterval(() => {
        if (startTimeRef.current !== null) {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setTimerSeconds(elapsed);
        } else {
          // Если startTimeRef не установлен, но таймер должен работать, устанавливаем его
          const currentSeconds = timerSeconds || 0;
          startTimeRef.current = Date.now() - (currentSeconds * 1000);
        }
      }, 100);            
    } else {
      // При паузе или остановке очищаем интервал
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;        
      }
      // При паузе сбрасываем startTimeRef чтобы предотвратить продолжение отсчета
      if (!isRunning && isPaused) {
        startTimeRef.current = null;
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [isRunning, isPaused]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getParticipantName = (participant: Participant | null | undefined): string => {
    if (!participant) return 'Ожидание участника';
    
    // Проверяем разные варианты структуры данных
    let firstName = (participant as any).firstName || (participant as any).first_name || '';
    let lastName = (participant as any).lastName || (participant as any).last_name || '';
    
    // Если не нашли напрямую, проверяем вложенный объект participant
    if ((!firstName || !lastName) && (participant as any).participant) {
      const participantData = (participant as any).participant;
      firstName = firstName || participantData.firstName || participantData.first_name || '';
      lastName = lastName || participantData.lastName || participantData.last_name || '';
    }
    
    // Если все еще не нашли, проверяем вложенный объект user
    if ((!firstName || !lastName) && (participant as any).user) {
      const user = (participant as any).user;
      firstName = firstName || user.firstName || user.first_name || '';
      lastName = lastName || user.lastName || user.last_name || '';
    }
    
    if (firstName && lastName) {
      return `${lastName.toUpperCase()} ${firstName.toUpperCase()}`;
    }
    if (lastName) return lastName.toUpperCase();
    if (firstName) return firstName.toUpperCase();
    return 'Ожидание участника';
  };

  const getParticipantClub = (participant: Participant | null | undefined): string => {
    if (!participant) return '';
    
    // Проверяем разные варианты структуры данных для клуба
    const club = (participant as any)?.club?.name 
      || (participant as any)?.Club?.name
      || (participant as any)?.clubName 
      || (participant as any)?.user?.club?.name
      || (participant as any)?.user?.clubName
      || ((participant as any)?.clubParticipants && (participant as any).clubParticipants[0]?.club?.name)
      || ((participant as any)?.participant?.club?.name)
      || ((participant as any)?.participant?.clubName)
      || '';
    
    return club;
  };

  // Скрываем элементы интерфейса браузера через CSS и мета-теги
  useEffect(() => {
    // Добавляем мета-теги для скрытия элементов интерфейса на мобильных
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (viewportMeta) {
      viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, minimal-ui');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, minimal-ui';
      document.head.appendChild(meta);
    }
    
    // Скрываем скроллбары
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    
    // Пытаемся скрыть адресную строку через прокрутку вниз (работает в некоторых браузерах)
    setTimeout(() => {
      window.scrollTo(0, 1);
    }, 100);
    
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
    };
  }, []);

  const coverUrl = tournamentInfo?.coverImage ? getCoverImageUrl(tournamentInfo.coverImage) : undefined;

  return (
    <BroadcastContainer>
      <style>{`
        /* Скрываем все элементы интерфейса браузера */
        body {
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden !important;
        }
        html {
          overflow: hidden !important;
        }
        /* Скрываем скроллбары */
        ::-webkit-scrollbar {
          display: none !important;
        }
        * {
          -ms-overflow-style: none !important;
          scrollbar-width: none !important;
        }
      `}</style>
      {(tournamentInfo?.name || coverUrl) && (
        <BroadcastHeader>
          {coverUrl && <TournamentLogo src={coverUrl} alt="" />}
          {tournamentInfo?.name && <TournamentName>{tournamentInfo.name}</TournamentName>}
        </BroadcastHeader>
      )}
      <TimerSection>
        {tatamiNumber && (
          <TatamiLabel>Татами {tatamiNumber}</TatamiLabel>
        )}
        <TimerDisplay>{formatTime(timerSeconds)}</TimerDisplay>
      </TimerSection>
      
      <MainContent>
        <CurrentMatchSection>
          <SectionTitle>
            Текущий бой
            {currentMatch && ` (Бой #${currentDisplayNum ?? currentMatch.matchNumber ?? currentMatch.categoryOrderOnTatami ?? currentMatch.id})`}
          </SectionTitle>
          {currentMatch?.category?.name && (
            <CategoryLabel>{currentMatch.category.name}</CategoryLabel>
          )}
          {currentMatch ? (
            <ParticipantsContainer>
              <ParticipantCard>
                <ParticipantName>
                  {getParticipantName(currentMatch.participant1 as Participant)}
                </ParticipantName>
                <ParticipantClub>
                  {getParticipantClub(currentMatch.participant1 as Participant)}
                </ParticipantClub>
              </ParticipantCard>
              
              <VS>VS</VS>
              
              <ParticipantCard>
                <ParticipantName>
                  {getParticipantName(currentMatch.participant2 as Participant)}
                </ParticipantName>
                <ParticipantClub>
                  {getParticipantClub(currentMatch.participant2 as Participant)}
                </ParticipantClub>
              </ParticipantCard>
            </ParticipantsContainer>
          ) : !tatamiNumber && searchParams.get('tatami') === null ? (
            <EmptyState>Укажите татами в URL: ?tatami=1</EmptyState>
          ) : (
            <EmptyState>Ожидание начала боя</EmptyState>
          )}
        </CurrentMatchSection>
        
        <NextMatchSection>
          <NextMatchTitle>Следующий бой</NextMatchTitle>
          {nextMatch?.category?.name && (
            <NextMatchCategory>{nextMatch.category.name}</NextMatchCategory>
          )}
          {nextMatch ? (
            <NextMatchInfo>
              <div style={{ flex: 1, textAlign: 'center' }}>
                {nextMatch.participant1Id ? getParticipantName(nextMatch.participant1 as Participant) : 'Ожидание участника'}
              </div>
              <div style={{ margin: '0 20px' }}>VS</div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                {nextMatch.participant2Id ? getParticipantName(nextMatch.participant2 as Participant) : 'Ожидание участника'}
              </div>
              <div style={{ marginLeft: 'auto', opacity: 0.8, minWidth: '80px', textAlign: 'right' }}>
                Бой #{nextDisplayNum ?? nextMatch.matchNumber ?? nextMatch.categoryOrderOnTatami ?? nextMatch.id}
              </div>
            </NextMatchInfo>
          ) : (
            <EmptyState>Нет запланированных боев</EmptyState>
          )}
        </NextMatchSection>
      </MainContent>
    </BroadcastContainer>
  );
};
